"use client";

import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import Label from "@/components/form/Label";
import Input from "@/components/form/input/InputField";
import { twMerge } from "tailwind-merge";

export default function SearchableSelect({
  label,
  value,
  onChange,
  options,
  getLabel = (o) => o.name,
  getValue = (o) => o.id,
  getSubLabel,
  placeholder = "Search…",
  createHint,
  onPickCreate,
  disabled,
  /** Tailwind z-index for click-away layer (e.g. z-[100002] inside DrawerPanel) */
  overlayZClass = "z-40",
  /** Tailwind z-index for options list (should be above overlayZClass) */
  menuZClass = "z-50",
  /** Forward to the inner search input (focus / accessibility). */
  inputId,
  autoFocus = false,
  /** Extra classes for the inner search input (e.g. compact height). */
  inputClassName = "",
}) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [menuRect, setMenuRect] = useState(null);
  const [mounted, setMounted] = useState(false);
  const anchorRef = React.useRef(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  const updateMenuRect = React.useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const viewportHeight = window.innerHeight || 0;
    const viewportPadding = 12;
    const gap = 6;
    const preferredMenuHeight = 240;
    const availableBelow = Math.max(0, viewportHeight - rect.bottom - viewportPadding - gap);
    const availableAbove = Math.max(0, rect.top - viewportPadding - gap);
    const openUpward =
      availableBelow < 140 && availableAbove > availableBelow;
    const maxHeight = Math.max(
      64,
      Math.min(preferredMenuHeight, openUpward ? availableAbove : availableBelow)
    );
    setMenuRect({
      top: openUpward ? null : rect.bottom + gap,
      bottom: openUpward ? viewportHeight - rect.top + gap : null,
      left: rect.left,
      width: rect.width,
      maxHeight,
      openUpward,
    });
  }, []);

  // Sync display from controlled `value`, but not while the menu is open — typing
  // calls `onChange(null)` so parents can clear selection, which would otherwise
  // clear `value` and this effect would wipe `q` on every keystroke.
  useEffect(() => {
    if (open) return;
    const sel = options.find((o) => getValue(o) === value || String(getValue(o)) === String(value));
    if (sel) setQ(getLabel(sel));
    else if (value === null || value === undefined || value === "") setQ("");
  }, [value, options, getLabel, getValue, open]);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return options.slice(0, 50);
    return options
      .filter((o) => getLabel(o).toLowerCase().includes(qq))
      .slice(0, 50);
  }, [options, q, getLabel]);

  const showCreate =
    onPickCreate &&
    createHint &&
    q.trim() &&
    !options.some((o) => getLabel(o).toLowerCase() === q.trim().toLowerCase());

  useEffect(() => {
    if (!open) {
      setHighlightedIndex(-1);
      return;
    }
    setHighlightedIndex(filtered.length > 0 ? 0 : -1);
  }, [open, q, filtered.length]);

  useEffect(() => {
    if (!open) return;
    const sync = () => updateMenuRect();
    sync();
    window.addEventListener("resize", sync);
    window.addEventListener("scroll", sync, true);
    return () => {
      window.removeEventListener("resize", sync);
      window.removeEventListener("scroll", sync, true);
    };
  }, [open, updateMenuRect]);

  return (
    <div ref={anchorRef} className="relative">
      {label && <Label>{label}</Label>}
      <Input
        id={inputId}
        autoFocus={autoFocus}
        value={q}
        disabled={disabled}
        placeholder={placeholder}
        className={twMerge("h-11 text-base px-4 py-2.5", inputClassName)}
        onFocus={() => {
          updateMenuRect();
          setOpen(true);
        }}
        onChange={(e) => {
          setQ(e.target.value);
          updateMenuRect();
          setOpen(true);
          onChange(null);
        }}
        onKeyDown={(e) => {
          if (!open) return;
          if (e.key === "Tab") {
            e.preventDefault();
            if (!filtered.length) return;
            setHighlightedIndex((prev) => {
              const size = filtered.length;
              if (size === 0) return -1;
              if (prev < 0) return e.shiftKey ? size - 1 : 0;
              return e.shiftKey ? (prev - 1 + size) % size : (prev + 1) % size;
            });
            return;
          }
          if (e.key === "ArrowDown") {
            e.preventDefault();
            if (!filtered.length) return;
            setHighlightedIndex((prev) =>
              prev < 0 ? 0 : (prev + 1) % filtered.length
            );
            return;
          }
          if (e.key === "ArrowUp") {
            e.preventDefault();
            if (!filtered.length) return;
            setHighlightedIndex((prev) =>
              prev < 0 ? filtered.length - 1 : (prev - 1 + filtered.length) % filtered.length
            );
            return;
          }
          if (e.key === "Enter") {
            e.preventDefault();
            if (!filtered.length) return;
            const idx = highlightedIndex >= 0 ? highlightedIndex : 0;
            const picked = filtered[idx];
            if (!picked) return;
            onChange(getValue(picked));
            setQ(getLabel(picked));
            setOpen(false);
          }
          if (e.key === "Escape") {
            setOpen(false);
          }
        }}
      />
      {open && !disabled && mounted &&
        createPortal(
          <>
            <button
              type="button"
              tabIndex={-1}
              className={`fixed inset-0 cursor-default ${overlayZClass}`}
              aria-hidden
              onClick={() => setOpen(false)}
            />
            <ul
              className={`fixed max-h-60 overflow-auto rounded-lg border border-gray-800 bg-gray-950 py-1.5 shadow-xl ${menuZClass}`}
              style={{
                top: menuRect?.openUpward ? "auto" : (menuRect?.top ?? 0),
                bottom: menuRect?.openUpward ? (menuRect?.bottom ?? 0) : "auto",
                left: menuRect?.left ?? 0,
                width: menuRect?.width ?? 320,
                maxHeight: menuRect?.maxHeight ?? 240,
              }}
            >
              {filtered.map((o, idx) => (
                <li key={getValue(o)}>
                  <button
                    type="button"
                    className={`flex w-full flex-col items-start px-3.5 py-2.5 text-left text-sm text-gray-200 ${
                      idx === highlightedIndex ? "bg-white/5" : "hover:bg-white/5"
                    }`}
                    onMouseEnter={() => setHighlightedIndex(idx)}
                    onClick={() => {
                      onChange(getValue(o));
                      setQ(getLabel(o));
                      setOpen(false);
                    }}
                  >
                    <span>{getLabel(o)}</span>
                    {getSubLabel?.(o) && (
                      <span className="text-xs text-gray-500">{getSubLabel(o)}</span>
                    )}
                  </button>
                </li>
              ))}
              {showCreate && (
                <li>
                  <button
                    type="button"
                    className="w-full px-3.5 py-2.5 text-left text-sm font-medium text-brand-400 hover:bg-white/5"
                    onClick={() => {
                      onPickCreate(q.trim());
                      setOpen(false);
                    }}
                  >
                    + {createHint}
                  </button>
                </li>
              )}
            </ul>
          </>,
          document.body
        )}
    </div>
  );
}
